export const RECORDING_CATEGORY = {
    code: "GO",
    label: "Gottesdienst",
} as const;


export type RecordingPerson = {
    key: string;
    name: string;
    surname: string;
};

export type RecordingSermon = {
    preacher: RecordingPerson | null;
    topic: string;
    biblePassage: string;
};

export type RecordingChoirSong = {
    slot: number;
    name: string;
};

export type RecordingServiceData = {
    serviceType?: string;
    serviceLabel?: string;
    serviceDate: string;
    categoryCode?: string;
    categoryLabel?: string;
    locationCode?: string;
    locationLabel?: string;
    sermon1: RecordingSermon;
    sermon2: RecordingSermon | null;
    choirSongs: RecordingChoirSong[];
};

export type RecordingTrack = {
    slot: number;
    sourceLabel: string;
    generatedName: string;
};

function normalizeText(value: string): string {
    return value
        .replace(/[\r\n]+/g, " ")
        .replace(/[/_]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
}

function formatTrackNumber(value: number): string {
    return String(value).padStart(2, "0");
}

function buildContentLabel(parts: string[]): string {
    return parts.map(normalizeText).filter(Boolean).join("_");
}

function buildPersonLabel(person: RecordingPerson | null): string {
    if (!person) {
        return "Unbekannt";
    }

    return normalizeText(`${person.name} ${person.surname}`);
}

function buildSermonFolderPart(sermon: RecordingSermon | null): string {
    if (!sermon) {
        return "";
    }

    const contentParts = [sermon.topic, sermon.biblePassage].filter((part) => normalizeText(part).length > 0);
    const content = buildContentLabel(contentParts);
    const preacher = buildPersonLabel(sermon.preacher);

    return [content, preacher].filter(Boolean).join("_");
}

export function buildRecordingTrackNames(service: RecordingServiceData): RecordingTrack[] {
    const tracks: Array<{ sourceLabel: string; contentParts: string[]; performer: string }> = [];

    service.choirSongs
        .filter((song) => normalizeText(song.name).length > 0)
        .sort((a, b) => a.slot - b.slot)
        .forEach((song) => {
            tracks.push({
                sourceLabel: `Chorlied ${song.slot}`,
                contentParts: [song.name],
                performer: "Chor",
            });
        });

    tracks.push({
        sourceLabel: "Predigt 1",
        contentParts: [service.sermon1.topic, service.sermon1.biblePassage].filter((part) => normalizeText(part).length > 0),
        performer: buildPersonLabel(service.sermon1.preacher),
    });

    if (service.sermon2) {
        tracks.push({
            sourceLabel: "Predigt 2",
            contentParts: [service.sermon2.topic, service.sermon2.biblePassage].filter((part) => normalizeText(part).length > 0),
            performer: buildPersonLabel(service.sermon2.preacher),
        });
    }

    return tracks
        .filter((track) => track.contentParts.length > 0)
        .map((track, index) => ({
            slot: index + 1,
            sourceLabel: track.sourceLabel,
            generatedName: `${formatTrackNumber(index + 1)}. ${buildContentLabel(track.contentParts)}_${normalizeText(track.performer)}`,
        }));
}

export function buildRecordingFolderName(service: RecordingServiceData, trackCount?: number): string {
    const effectiveTrackCount = trackCount ?? buildRecordingTrackNames(service).length;
    const serviceVariant = normalizeText(service.serviceLabel || "");
    const serviceBlock = [
        service.categoryCode?.trim() || RECORDING_CATEGORY.code,
        // The Gemeinde's recording location (GOD-46), always sent by getRecordingServiceData.
        service.locationCode?.trim(),
        normalizeText(service.serviceDate),
        serviceVariant,
    ].filter(Boolean).join("-");

    const sermon1Part = buildSermonFolderPart(service.sermon1);
    const sermon2Part = buildSermonFolderPart(service.sermon2);

    return [serviceBlock, sermon1Part, sermon2Part, String(effectiveTrackCount)].filter(Boolean).join("-");
}
